import { useEffect, useRef, useState } from "react";
import axiosInstance from "src/common/axiosInstance";

function localTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function ActivitySchedulePanel({ activityId, initialOperation = "reschedule", onClose, onSaved }) {
  const [context, setContext] = useState(null);
  const [operation, setOperation] = useState(initialOperation);
  const [scheduledAt, setScheduledAt] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const [reload, setReload] = useState(0);
  const saving = useRef(false);
  const heading = useRef(null);

  useEffect(() => {
    let active = true;
    heading.current?.focus();
    setContext(null);
    setError("");
    setStale(false);
    axiosInstance.get(`/lead-activities/${activityId}/follow-up-context`).then(({ data }) => {
      if (!active) return;
      setContext(data.data);
      setScheduledAt(localTime(data.data.activity.scheduled_at));
    }).catch(() => {
      if (active) setError("Unable to load this activity. Check your access and try again.");
    });
    return () => { active = false; };
  }, [activityId, reload]);

  async function save(event) {
    event.preventDefault();
    if (!context || saving.current || stale) return;
    const payload = { operation, context_token: context.context_token };
    if (operation === "reschedule") {
      const date = new Date(scheduledAt);
      if (Number.isNaN(date.getTime())) { setError("Choose a valid date and time."); return; }
      payload.scheduled_at = date.toISOString().replace(/\.\d{3}Z$/, "+00:00");
    }
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      await axiosInstance.patch(`/lead-activities/${activityId}/follow-up-context`, payload);
      onSaved();
    } catch (failure) {
      // An uncertain response must be reviewed, never automatically replayed.
      setStale(true);
      setError(failure.response?.status === 409
        ? "This activity changed. Reload and review its current details before saving again."
        : "The change could not be confirmed. Reload to check its current status before trying again.");
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }

  return <section aria-label="Manage activity" className="rounded-2xl border border-blue-200 bg-blue-50 p-5 space-y-4">
    <h3 ref={heading} tabIndex={-1} className="font-semibold">Manage activity {activityId}</h3>
    <p className="text-sm text-slate-600">Update this CRM activity. Linked reminder plans recheck its schedule and status before dispatch; an action already in progress may still finish.</p>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {!context && !error && <p role="status">Loading activity…</p>}
    {context && <form onSubmit={save} className="space-y-3">
      <p>Current status: {context.activity.status}</p>
      {context.activity.scheduled_at && <p>Scheduled: {new Date(context.activity.scheduled_at).toLocaleString()}</p>}
      {context.activity.status === "PENDING" && <>
        <label className="block">Change
          <select aria-label="Change" value={operation} onChange={event => setOperation(event.target.value)} disabled={busy || stale} className="block rounded border p-2">
            <option value="reschedule">Reschedule</option><option value="complete">Complete</option><option value="cancel">Cancel activity</option>
          </select>
        </label>
        {operation === "reschedule" && <label className="block">Date and time ({Intl.DateTimeFormat().resolvedOptions().timeZone})
          <input aria-label="Date and time" type="datetime-local" required value={scheduledAt} onChange={event => setScheduledAt(event.target.value)} disabled={busy || stale} className="block rounded border p-2" />
        </label>}
        <button type="submit" disabled={busy || stale} className="rounded bg-blue-700 px-4 py-2 text-white disabled:opacity-50">{busy ? "Saving…" : "Save change"}</button>
      </>}
    </form>}
    {(error || stale) && <button type="button" disabled={busy} onClick={() => setReload(value => value + 1)} className="rounded border px-3 py-2">Reload activity</button>}
    <button type="button" disabled={busy} onClick={onClose} className="rounded border px-3 py-2 ml-2">Close</button>
  </section>;
}
